import { Button } from '@creditornot/cb-button';
import React, { useState } from 'react';
import { Form, useField } from 'react-final-form';
import { FormattedMessage } from 'react-intl';

import { WoltModal } from 'common/components';
import validateYupSchema from 'common/utils/validateYupSchema';
import { reportEvent } from 'modules/campaigns/behavioral';
import { AdCampaignBudgetFormValues, AdCampaignBudgetType } from 'modules/campaigns/types';

import { AdCampaignBudgetFormModal } from './AdCampaignBudgetFormModal/AdCampaignBudgetFormModal';
import { budgetValidationSchema } from './AdCampaignBudgetFormModal/budgetAndBidValidationSchema';
import { AdsCampaignWeeklyBudgetSelector } from './AdCampaignWeeklyBudgetSelector';
import CampaignFormItem from '../../CampaignFormItem';
import UnsavedChangesModal from '../../modals/UnsavedChangesModal/UnsavedChangesModal';
import { BudgetOption } from '../types';

export type AdCampaignBudgetFormItemProps = {
  initialValues: AdCampaignBudgetFormValues;
  isDisabled: boolean;
  budgetOptions: BudgetOption[];
  campaignId?: string;
  hasCampaignStarted?: boolean;
  'data-testid'?: string;
  maxBidAmount: number;
  minBidAmount: number;
  minWeeklyBudget: number;
  maxWeeklyBudget: number;
  onUpdate(formValues: AdCampaignBudgetFormValues): void;
};

export const AdCampaignBudgetFormItem = ({
  initialValues,
  budgetOptions,
  isDisabled,
  campaignId,
  hasCampaignStarted,
  maxBidAmount,
  minBidAmount,
  minWeeklyBudget,
  maxWeeklyBudget,
  onUpdate,
}: AdCampaignBudgetFormItemProps) => {
  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState<boolean>(false);
  const { budgetAmount, customBidAmount, bidStrategy } = initialValues;
  const {
    input: { value: budgetTypeSyncedValue },
  } = useField('budgetType');

  const handleCloseModals = (isFormDirty?: boolean) => {
    if (isFormDirty) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      setIsUnsavedChangesModalOpen(false);
      setIsBudgetModalOpen(false);
    }
  };

  const handleSubmitForm = (formData: AdCampaignBudgetFormValues) => {
    onUpdate({
      budgetType: formData.budgetType,
      budgetAmount: formData.budgetAmount,
      customBidAmount: formData.bidStrategy === 'custom' ? formData.customBidAmount : undefined,
      bidStrategy: formData.bidStrategy,
    });
    handleCloseModals();
  };

  const handleValidate = (values: AdCampaignBudgetFormValues) => {
    return validateYupSchema(budgetValidationSchema, {
      maxBidAmount,
      minBidAmount,
      minWeeklyBudget,
      maxWeeklyBudget,
    })(values);
  };

  const handleBudgetCtaClick = () => {
    setIsBudgetModalOpen(true);
    reportEvent('NEW_AD_CAMPAIGN_WEEKLY_BUDGET_BUTTON');
  };

  return (
    <>
      <Form<AdCampaignBudgetFormValues>
        initialValues={{
          customBidAmount,
          budgetAmount,
          budgetType: budgetTypeSyncedValue,
          bidStrategy: customBidAmount ? 'custom' : 'auto',
        }}
        onSubmit={handleSubmitForm}
        validate={values => handleValidate(values)}
      >
        {({ handleSubmit, form, dirty, errors }) => {
          return (
            <WoltModal isOpen={isBudgetModalOpen} onRequestClose={() => handleCloseModals(dirty)}>
              <AdCampaignBudgetFormModal
                budgetOptions={budgetOptions}
                customBidAmount={customBidAmount}
                onSubmit={handleSubmit}
                onCloseClick={() => handleCloseModals(dirty)}
                isCampaignCompleted={isDisabled}
                errors={errors}
                maxBidAmount={maxBidAmount}
                minBidAmount={minBidAmount}
                minWeeklyBudget={minWeeklyBudget}
                maxWeeklyBudget={maxWeeklyBudget}
                budgetAmount={budgetAmount}
                budgetType={budgetTypeSyncedValue}
                isDirty={dirty}
                hasCampaignStarted={hasCampaignStarted}
              />
              <UnsavedChangesModal
                isOpen={dirty && isUnsavedChangesModalOpen}
                onDropChanges={() => {
                  handleCloseModals();
                  form.reset();
                }}
                onClose={() => setIsUnsavedChangesModalOpen(false)}
              />
            </WoltModal>
          );
        }}
      </Form>
      <CampaignFormItem
        data-testid="budget-field-item"
        title={<FormattedMessage id="campaigns.ads.budget.weekly-budget" />}
        tooltip={{
          description: <FormattedMessage id="campaigns.form.budget.tooltip" />,
        }}
        value={
          <AdsCampaignWeeklyBudgetSelector
            budgetOptions={budgetOptions.filter(option => option.type !== 'custom')}
            onChange={(budgetType: AdCampaignBudgetType) =>
              onUpdate({ budgetAmount, bidStrategy, customBidAmount, budgetType })
            }
            isDisabled={isDisabled}
            campaignId={campaignId}
            budgetType={budgetTypeSyncedValue}
            budgetAmount={budgetAmount}
            bidAmount={customBidAmount}
          />
        }
        action={
          <Button variant="lightBlue" size="small" disabled={isDisabled} onClick={handleBudgetCtaClick}>
            {!!campaignId ? (
              <FormattedMessage id="campaigns.ads.budget.edit-budget" />
            ) : (
              <FormattedMessage id="campaigns.form.more-options" />
            )}
          </Button>
        }
      />
    </>
  );
};
